/**
 * Home Screen — fixed header with sub-tabs.
 * Mobile: 10px padding, tabs horizontally scrollable.
 * Desktop: 40px padding (30+10), tabs left-aligned + premium CTA right.
 * Telegram fullscreen safe area respected via tg-header-safe-area.
 */

import React, { useRef, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Lock, Crown } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useHomeStore } from '@turnifly/shared/stores'
import { usePomodoroStore } from '@turnifly/shared/stores'
import { HabitsTab } from './habits-tab'
import { HabitFab } from './habit-fab'
import { HabitCreateBottomSheet } from './habit-create-bottom-sheet'
import { HabitProfileSheet } from './habit-profile-sheet'
import { PomodoroTab } from './pomodoro/pomodoro-tab'
import { GoalsTab } from './goals/goals-tab'
import { LifeTab } from './life/life-tab'
import { PremiumPage } from '../../profile/components/premium-page'

type HomeTabId = 'habits' | 'goals' | 'life' | 'pomodoro'

interface TabConfig {
  id: HomeTabId
  labelKey: string
  isPremium: boolean
}

const TABS: TabConfig[] = [
  { id: 'habits', labelKey: 'home.habits', isPremium: false },
  { id: 'goals', labelKey: 'home.goals', isPremium: true },
  { id: 'life', labelKey: 'home.life', isPremium: true },
  { id: 'pomodoro', labelKey: 'home.pomodoro', isPremium: true },
]

const TAB_ORDER: HomeTabId[] = ['habits', 'goals', 'life', 'pomodoro']
const SWIPE_THRESHOLD = 60

export const HomeScreen: React.FC = () => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<HomeTabId>('habits')
  const touchRef = useRef<{ x: number; y: number } | null>(null)
  const { user } = useAuthStore()
  const isPremium = user?.is_premium ?? false
  const viewingHabitId = useHomeStore((s) => s.viewingHabitId)
  const setViewingHabitId = useHomeStore((s) => s.setViewingHabitId)
  const isImmersive = usePomodoroStore((s) => s.status === 'running' && s.mode === 'work')

  const switchTab = useCallback((direction: 'next' | 'prev'): void => {
    const idx = TAB_ORDER.indexOf(activeTab)
    const nextIdx = direction === 'next' ? idx + 1 : idx - 1
    if (nextIdx >= 0 && nextIdx < TAB_ORDER.length) {
      setActiveTab(TAB_ORDER[nextIdx])
    }
  }, [activeTab])

  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent): void => {
    if (!touchRef.current) return
    const dx = e.changedTouches[0].clientX - touchRef.current.x
    const dy = e.changedTouches[0].clientY - touchRef.current.y
    touchRef.current = null
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy) * 1.5) return
    const target = e.target as HTMLElement
    if (target.closest('.habit-grid-wrapper')) return
    switchTab(dx < 0 ? 'next' : 'prev')
  }, [switchTab])

  return (
    <div className="relative h-[100dvh]">
      {/* ── Fixed header (hidden in immersive pomodoro) ── */}
      {!isImmersive && <div className="fixed top-0 left-0 right-0 z-40 tg-header-safe-area">
        <div className="max-w-[1700px] mx-auto w-full flex items-center justify-center lg:justify-start h-11 px-2.5">
          {/* Tabs — pill stays fixed, content scrolls inside on mobile */}
          <HomeTabs activeTab={activeTab} onTabChange={setActiveTab} />

          {/* Spacer — desktop only, pushes Premium CTA to right */}
          <div className="hidden lg:block flex-1" />

          {/* Premium CTA — desktop only, right side, free users only */}
          {!isPremium && (
            <button className="hidden lg:flex items-center gap-2 shrink-0 h-11 px-4 rounded-full bg-[#58CC02]/[0.08] border border-[#58CC02]/20 hover:bg-[#58CC02]/[0.14] hover:border-[#58CC02]/30 transition-colors tap-highlight-transparent group">
              <Crown size={16} fill="currentColor" className="text-[#89E219] transition-transform duration-300 group-hover:scale-110" />
              <span className="text-[13px] font-semibold bg-gradient-to-r from-[#58CC02] to-[#89E219] bg-clip-text text-transparent">
                Premium
              </span>
            </button>
          )}
        </div>
      </div>}

      {/* ── Scrollable content (non-pomodoro) ── */}
      {activeTab !== 'pomodoro' && (
        <div
          className="h-full overflow-y-auto overscroll-y-contain pt-[125px] lg:pt-[110px] pb-[90px]"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-full max-w-[1700px] mx-auto px-2.5">
            {activeTab === 'habits' && <HabitsTab />}
            {activeTab === 'goals' && (isPremium ? <GoalsTab /> : <PremiumPlaceholder title={t('home.goals')} />)}
            {activeTab === 'life' && (isPremium ? <LifeTab /> : <PremiumPlaceholder title={t('home.life')} />)}
          </div>
        </div>
      )}

      {/* ── Pomodoro — full-screen immersive (premium only) ── */}
      {activeTab === 'pomodoro' && (isPremium ? (
        <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <PomodoroTab />
        </div>
      ) : (
        <div
          className="h-full overflow-y-auto pt-[125px] lg:pt-[110px] pb-[90px]"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-full max-w-[1700px] mx-auto px-2.5">
            <PremiumPlaceholder title={t('home.pomodoro')} />
          </div>
        </div>
      ))}

      <HabitCreateBottomSheet />
      {activeTab === 'habits' && <HabitFab />}

      {/* Habit profile overlay */}
      {viewingHabitId && (
        <HabitProfileSheet habitId={viewingHabitId} onClose={() => setViewingHabitId(null)} />
      )}
    </div>
  )
}

/* ── Tabs — extracted to avoid duplication ── */

interface HomeTabsProps {
  activeTab: HomeTabId
  onTabChange: (id: HomeTabId) => void
}

const HomeTabs: React.FC<HomeTabsProps> = ({ activeTab, onTabChange }) => {
  const { t } = useTranslation()
  const userIsPremium = useAuthStore((s) => s.user?.is_premium ?? false)
  return (
    <div className="inline-flex items-center h-11 px-1 rounded-full bg-white/[0.06] backdrop-blur-xl border border-white/[0.09] max-w-full overflow-x-auto scrollbar-hide">
      {TABS.map((tab) => {
        const isActive = activeTab === tab.id
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`relative shrink-0 px-3 py-2 rounded-full whitespace-nowrap text-[13px] font-semibold transition-colors tap-highlight-transparent ${
              isActive ? 'text-text' : 'text-text-hint hover:text-text'
            }`}
          >
            {isActive && (
              <motion.div
                layoutId="homeTabPill"
                className="absolute inset-0 bg-white/[0.08] rounded-full border border-white/[0.09]"
                transition={{ type: 'spring', stiffness: 350, damping: 28 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-1">
              {t(tab.labelKey)}
              {tab.isPremium && !userIsPremium && <Lock size={11} className="text-text-hint" />}
            </span>
          </button>
        )
      })}
    </div>
  )
}

const PremiumPlaceholder: React.FC<{ title: string }> = ({ title }) => {
  const { t } = useTranslation()
  const [showPremium, setShowPremium] = useState(false)

  return (
    <>
      <div className="flex items-center justify-center p-8 min-h-[60vh]">
        <button
          onClick={() => setShowPremium(true)}
          className="glass p-8 rounded-2xl max-w-sm w-full text-center tap-highlight-transparent hover:bg-white/[0.04] transition-colors"
        >
          <Crown className="w-12 h-12 text-primary mx-auto mb-4" />
          <h3 className="text-xl font-display font-bold mb-2">{title}</h3>
          <p className="text-text-hint text-sm mb-4">Premium</p>
          <div className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary/10 text-primary text-[13px] font-semibold">
            <Crown size={14} />
            {t('premium.upgrade')}
          </div>
        </button>
      </div>
      <PremiumPage isOpen={showPremium} onClose={() => setShowPremium(false)} />
    </>
  )
}
